import { FC } from "react"
import { useTranslation } from "react-i18next"
import InfiniteScroll from "react-infinite-scroller"

import { Flex, FlexItem } from "components/Flex"
import { Grid, GridItem } from "components/Grid"
import Button from "components/Button/Button"
import Modal from "components/Modal/Modal"
import Spinner from "components/Spinner/Spinner"
import TextBuildingBlock, { TextBuildingBlockMode } from "./TextBuildingBlock"
import TextBuildingBlocksFilter from "./TextBuildingBlocksFilter"
import useTextBuildingBlocksModal from "./useTextBuildingBlocksModal"
import TextBuildingBlockModal from "./TextBuildingBlockModal"
import { InfoDropdown, InfoDropdownList, InfoDropdownListItemButton } from "components/InfoDropdown"
import Text from "components/Text/Text"

interface ITextBuildingBlocksModalProps {
    mode: TextBuildingBlockMode
    notFormattedOnly?: boolean
    title?: string
    onSelectionInsert?(text: string): void
    onClose(): void
}

const TextBuildingBlocksModal: FC<ITextBuildingBlocksModalProps> = ({
    mode,
    notFormattedOnly,
    title,
    onSelectionInsert,
    onClose,
}) => {
    const { t } = useTranslation()
    const {
        isAddingTextFormatting,
        isLoading,
        handleAddClick,
        selectedBlocks,
        handleSelectionInsert,
        handleFilterChange,
        textBuildingBlocks,
        scrollEl,
        loadNextPage,
        isInitialLoad,
        hasMore,
        handleAddCancel,
        handleAdd,
        handleUpdate,
        handleDelete,
        handleBlockSelectToggle,
        isFiltered,
    } = useTextBuildingBlocksModal({
        onSelectionInsert,
        onClose,
        notFormattedOnly,
    })

    return (
        <>
            <Modal
                title={title || t("textBuildingBlocks.title")}
                maxWidth="952px"
                onClose={onClose}
                footer={
                    <Flex justifyContent="flex-end">
                        {mode === TextBuildingBlockMode.manage &&
                            <InfoDropdown
                                label={<Button label={t("common.addItem")} />}
                                maxWidth="380px"
                                placement="top-end"
                                content={closeAddItemDropdown => (
                                    <InfoDropdownList>
                                        <InfoDropdownListItemButton
                                            onClick={() => { closeAddItemDropdown(); handleAddClick(true) }}>
                                            <Text>
                                                {t("textBuildingBlocks.textBuildingBlock")}
                                                <div className="mt-half color-grey-500">
                                                    {t("textBuildingBlocks.createTextBuildingBlockWithFormatting")}
                                                </div>
                                            </Text>
                                        </InfoDropdownListItemButton>
                                        <InfoDropdownListItemButton
                                            onClick={() => { closeAddItemDropdown(); handleAddClick(false) }}>
                                            <Text>
                                                {t("textBuildingBlocks.textBuildingBlockWithoutFormatting")}
                                                <div className="mt-half color-grey-500">
                                                    {t("textBuildingBlocks.createTextBuildingBlockWithoutFormatting")}
                                                </div>
                                            </Text>
                                        </InfoDropdownListItemButton>
                                    </InfoDropdownList>
                                )}
                            />
                        }
                        {mode === TextBuildingBlockMode.select &&
                            <>
                                <Button
                                    theme="secondary"
                                    label={t("common.cancel")}
                                    onClick={onClose} />
                                <Button
                                    className="ml-2"
                                    disabled={selectedBlocks.length === 0}
                                    label={selectedBlocks.length > 0 ? t("textBuildingBlocks.insertXBuildingBlocks", { count: selectedBlocks.length }) : t("textBuildingBlocks.insertBuildingBlock")}
                                    onClick={handleSelectionInsert} />
                            </>
                        }
                    </Flex>
                }>

                <Flex flow="column" className="max-h-100 overflow-hidden mx--4 px-4">
                    {(isFiltered || (textBuildingBlocks && textBuildingBlocks.length > 0)) &&
                        <Flex justifyContent="space-between" alignItems="center" className="mb-4">
                            <FlexItem className="color-grey-500">
                                {mode === TextBuildingBlockMode.select && t('textBuildingBlocks.xBuildingBlocksSelected', { count: selectedBlocks.length })}
                            </FlexItem>
                            <FlexItem className="w-50">
                                <TextBuildingBlocksFilter
                                    onChange={handleFilterChange}
                                />
                            </FlexItem>
                        </Flex>
                    }
                    {textBuildingBlocks && textBuildingBlocks.length > 0 &&
                        <>
                            <Grid columns={mode === TextBuildingBlockMode.select ? '3.2rem 10% 3.2rem auto' : '10% 3.2rem auto'} gap="2.4rem" className="mb-3 font-bold">
                                {mode === TextBuildingBlockMode.select &&
                                    <GridItem></GridItem>
                                }
                                <GridItem>{t("textBuildingBlocks.id")}</GridItem>
                                <GridItem></GridItem>
                                <GridItem>{t("textBuildingBlocks.text")}</GridItem>
                            </Grid>
                            <div className="max-h-100 overflow-auto mx--4 px-4" ref={scrollEl}>
                                <InfiniteScroll
                                    pageStart={0}
                                    loadMore={loadNextPage}
                                    initialLoad={isInitialLoad}
                                    hasMore={hasMore}
                                    getScrollParent={() => scrollEl?.current || null}
                                    useWindow={false}>
                                    {textBuildingBlocks?.map(item => (
                                        <TextBuildingBlock
                                            key={item.id}
                                            mode={mode}
                                            block={item}
                                            onUpdate={handleUpdate}
                                            onDelete={handleDelete}
                                            selected={selectedBlocks.some(block => block.id === item.id)}
                                            onSelectionToggle={handleBlockSelectToggle}
                                        />
                                    ))}
                                    {isLoading &&
                                        <Spinner className="py-4" />
                                    }
                                </InfiniteScroll>
                            </div>
                        </>
                    }
                    {(!!textBuildingBlocks && textBuildingBlocks.length === 0 && !isAddingTextFormatting && !isLoading) &&
                        <div className="pt-6 text-center">{isFiltered ? t("common.noResultsFound") : t("common.noItemsAddedYet")}</div>
                    }
                    {!textBuildingBlocks && isLoading &&
                        <Spinner className="py-4" />
                    }
                </Flex>
            </Modal>
            {isAddingTextFormatting !== null &&
                <TextBuildingBlockModal
                    textFormatting={isAddingTextFormatting}
                    onCreate={handleAdd}
                    onClose={handleAddCancel}
                />
            }
        </>
    )
}

export default TextBuildingBlocksModal
